/** Browser authority binding for the in-memory Goal-from-Feedback command. */
import { ApiError, apiFetch, captureFeedbackConversationBoundary, isCurrentResourceCommandScope } from './api';
import { pinnedScope, scopeQuery } from './coachingScope';
import type { CoachingScopeIntent } from './coachingScope';
import type { components } from './api.generated';
import type { ResourceReadScope } from './types';

type Schema = components['schemas'];
export type CoachingManualCommand = { action: 'create-goal-from-feedback'; body: Schema['CreateGoalFromFeedbackRequest'] };
export type CoachingManualResult = Schema['GoalFromFeedbackResult'];
export type CoachingManualState =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: CoachingManualResult };
type ManualFailure = 'rejected' | 'retired' | 'unknown';
type ManualRequest = { method: 'POST'; path: string; query: Record<string, string>; body: unknown };

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const decimal = (value: unknown): value is string => typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value)
  && BigInt(value) <= 9223372036854775807n;

/** Wire integers stay integers. A JS caller must reject unsafe int64s. */
function assertSafeWireNumbers(value: unknown): void {
  if (typeof value === 'number' && (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value)))) {
    throw new RangeError('unsafe_wire_number');
  }
  if (Array.isArray(value)) {
    for (const child of value) assertSafeWireNumbers(child);
  } else if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value)) assertSafeWireNumbers(child);
  }
}

function intent(scope: ResourceReadScope): CoachingScopeIntent {
  if (!scope.actorId || !scope.clubId || !scope.teamId || scope.seasonId === '') throw new RangeError('invalid_manual_scope');
  return { team_id: scope.teamId, season: scope.seasonId === null ? { mode: 'unseasoned' }
    : { mode: 'current', season_id: scope.seasonId } };
}

function goalRequest(selected: CoachingScopeIntent, body: Schema['CreateGoalFromFeedbackRequest']): ManualRequest {
  const query = scopeQuery(pinnedScope(selected));
  // Manual writes pin the actual season, never the mutable current-season alias.
  if (selected.season.mode === 'current') delete query.season_scope;
  return { method: 'POST', path: '/api/coach/goals', query, body };
}

function acknowledgement(value: unknown): CoachingManualResult {
  if (!value || typeof value !== 'object' || Object.keys(value).sort().join(',') !== 'goal_id,schema_version'
    || (value as { schema_version?: unknown }).schema_version !== 'goal-from-feedback-result.v1'
    || !decimal((value as { goal_id?: unknown }).goal_id)) throw new Error('invalid_manual_acknowledgement');
  return clone(value) as CoachingManualResult;
}

const abort = () => new DOMException('Manual coaching context changed', 'AbortError');

function classifyFailure(error: unknown): ManualFailure {
  const code = error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail
    ? error.detail.code : null;
  if (code === 'manual_operation_retired') return 'retired';
  if (code === 'manual_operation_unresolved') return 'unknown';
  return error instanceof ApiError && error.status >= 400 && error.status < 500 ? 'rejected' : 'unknown';
}

/** Capture one original command. No persistence, auto-dispatch or GET inference. */
export function captureCoachingManualCommand(scope: ResourceReadScope, command: CoachingManualCommand) {
  const captured = { ...scope }; const selected = intent(captured);
  const boundary = captureFeedbackConversationBoundary({ ...captured, view: 'coach' });
  const current = () => boundary.current() && isCurrentResourceCommandScope(captured);
  let original: CoachingManualCommand;
  let request: ManualRequest;
  try {
    assertSafeWireNumbers(command);
    original = clone(command);
    request = goalRequest(clone(selected), original.body);
  } catch (error) { boundary.dispose(); throw error; }
  let state: CoachingManualState = { status: 'ready' };
  let unresolved = false;
  let disposed = false;
  let inFlight: Promise<CoachingManualResult> | null = null;
  const live = () => !disposed && current();
  const assertLive = () => { if (!live()) throw abort(); };

  async function run(signal?: AbortSignal): Promise<CoachingManualResult> {
    state = { status: 'sending' };
    try {
      assertLive();
      if (signal?.aborted) throw abort();
      const sent = clone(request);
      let value: unknown;
      try {
        value = await apiFetch<unknown>(sent.path, { method: sent.method, query: sent.query, body: sent.body,
          scope: false, cache: 'no-store', signal });
      } finally { if (!live() || signal?.aborted) throw abort(); }
      assertSafeWireNumbers(value);
      const result = acknowledgement(value);
      state = { status: 'accepted', result }; unresolved = false;
      return clone(result);
    } catch (error) {
      const classification = error instanceof DOMException && error.name === 'AbortError' ? 'unknown' : classifyFailure(error);
      if (classification !== 'rejected') unresolved = true;
      state = classification === 'retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      // Never expose an earlier lifetime's error to a new authority.
      assertLive();
      throw error;
    }
  }

  return {
    getIntent(): CoachingManualCommand | null { return live() ? clone(original) : null; },
    getState(): CoachingManualState { return live() ? clone(state) : { status: 'unavailable' }; },
    send(signal?: AbortSignal): Promise<CoachingManualResult> {
      if (!live() || signal?.aborted) return Promise.reject(abort());
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve(clone(state.result));
      // Defer dispatch until inFlight is installed, including sync throws.
      inFlight = Promise.resolve().then(() => run(signal)).finally(() => { inFlight = null; });
      return inFlight;
    },
    dispose(): void { disposed = true; boundary.dispose(); },
  };
}
